/**
 * Camera-scherm (fase 2): invoerbron kiezen (camera of bestand),
 * veldkalibratie (4 hoeken + homografie), overlay, meetmodus (FPS, frames,
 * heap), lokale debugclips met labeling, en de vision-scoring (motion).
 */

import { h } from "./dom";
import type { App } from "./app";
import { calibrationFromCorners, drawCourtOverlay } from "../detect/camera";
import type { FieldPoint, LabeledClip } from "../detect/types";
import type { PhysicalSide } from "../core";
import { downloadBlob } from "../detect/clips";

export function renderCamera(app: App, root: HTMLElement): void {
  const video = app.activeVideo ?? h("video", { id: "cam-video", playsinline: "", muted: "" }) as HTMLVideoElement;
  const overlay = h("canvas", { id: "cam-overlay" }) as HTMLCanvasElement;
  const wrap = h("div", { class: "cam-wrap" }, [video, overlay]);

  const profiles: Record<string, { w: number; fps: number }> = {
    low: { w: 320, fps: 10 },
    mid: { w: 640, fps: 15 },
    high: { w: 960, fps: 24 },
  };
  const profile = profiles[app.config.perfProfile] ?? profiles.mid!;

  const statsEl = h("pre", { class: "stats" }, ["(start een bron)"]);
  const calStatus = h("p", { class: "muted small" });
  const visionEl = h("p", { class: "chip chip-info" }, [app.visionStatus || "Vision uit."]);

  const clipsList = h("div", { class: "clips-list" });
  let tapCorners: FieldPoint[] = [];
  let calibrating = false;
  let statsTimer = 0;
  let fileUrl = "";

  const btn = (label: string, fn: () => void, cls = "btn small"): HTMLButtonElement => h("button", { class: cls, onclick: fn }, [label]);

  const sourceKind = h("select", { id: "source-kind" }, [
    h("option", { value: "camera", selected: app.activeSourceKind !== "file" }, ["📷 Camera"]),
    h("option", { value: "file", selected: app.activeSourceKind === "file" }, ["🎬 Bestand (video)"]),
  ]);
  const camSelect = h("select", { id: "cam-select" });
  const fileInput = h("input", { type: "file", accept: "video/*", class: "small" });
  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    fileUrl = app.fileSource.loadFile(f);
    calStatus.textContent = `Bestand gekozen: ${f.name}`;
  });

  function refreshCams(): void {
    void app.cam.list().then((cams) => {
      camSelect.innerHTML = "";
      for (const c of cams) {
        camSelect.append(h("option", { value: c.deviceId }, [`${c.label}${c.facingEnvironment ? " (achter)" : ""}`]));
      }
      if (cams.length === 0) {
        camSelect.append(h("option", { value: "" }, ["(geen camera's gevonden)"]));
      }
    });
  }

  async function startSource(): Promise<void> {
    stopStats();
    const kind = sourceKind.value as "camera" | "file";
    const deviceId = kind === "camera" ? (camSelect.value || undefined) : (fileUrl || undefined);
    const res = await app.startSource(kind, deviceId);
    if (!res) {
      visionEl.textContent = app.visionStatus;
      return;
    }
    // Toon de actieve bron in de wrap.
    wrap.innerHTML = "";
    wrap.append(res.video, overlay);
    sizeCanvas(res.video);
    calibrating = false;
    tapCorners = [];
    app.clipCapture.dispose();
    if (app.clipCapture.supported && app.activeStream) app.clipCapture.start(app.activeStream, 1000);
    startOverlayLoop(res.video);
    startStats();
    calStatus.textContent = "";
    visionEl.textContent = app.visionStatus;
  }

  function stopSource(): void {
    app.stopSource();
    stopStats();
    visionEl.textContent = "Bron gestopt. Vision uit.";
  }

  function toggleVision(): void {
    if (app.visionOn) {
      app.stopVision();
      visionEl.textContent = "Vision uit.";
    } else {
      app.startVision();
      visionEl.textContent = app.visionStatus;
    }
  }

  function stopStats(): void {
    if (statsTimer) window.clearInterval(statsTimer);
    statsTimer = 0;
  }

  function startStats(): void {
    stopStats();
    statsTimer = window.setInterval(() => {
      const s = app.visionOn ? app.motion.loopStats : app.cameraStats();
      const mem = s.usedJSHeapMB !== null ? `${s.usedJSHeapMB.toFixed(0)} MB` : "n/a";
      const m = app.motion.status;
      const lines = [
        `effectieve FPS   : ${s.fps.toFixed(1)} (doel ${profile.fps})`,
        `frame-interval   : ${s.frameIntervalMs.toFixed(1)} ms`,
        `gemiste frames   : ${s.missedFrames}`,
        `verwerkt         : ${s.framesProcessed} frames`,
        `verwerkingstijd  : ${s.lastProcessMs.toFixed(1)} ms/frame`,
        `geheugen (heap)  : ${mem}`,
        `process-resolutie: ${profile.w}px breed`,
        ``,
        `detector         : ${app.visionOn ? "aan (motion)" : "uit"}`,
        `detector-status  : ${m.label}`,
        `motion-energie   : ${m.motionEnergy.toFixed(1)}`,
        `laatste opslag   : ${m.serveSide ?? "?"} (conf ${m.lastServeConfidence.toFixed(2)})`,
        `rally's / einden : ${m.rallyCount} / ${m.endCount}`,
      ];
      statsEl.textContent = lines.join("\n");
      visionEl.textContent = app.visionStatus || (app.visionOn ? "Vision aan." : "Vision uit.");
    }, 500);
  }

  function startOverlayLoop(vid: HTMLVideoElement): void {
    app.frameLoop.start(
      vid,
      (now) => {
        void now;
        const c = overlay.getContext("2d");
        if (!c) return;
        const cal = app.calibration;
        const teamSides: Record<PhysicalSide, string> = {
          left: cal?.teamsOnSides.left === "A" || cal?.teamsOnSides.left === "B" ? app.resolveConfig().teams[cal.teamsOnSides.left].name : "?",
          right: cal?.teamsOnSides.right === "A" || cal?.teamsOnSides.right === "B" ? app.resolveConfig().teams[cal.teamsOnSides.right].name : "?",
        };
        drawCourtOverlay(c, vid, cal, { showZones: true, teamSides });
        if (calibrating) {
          c.fillStyle = "rgba(255,220,0,0.9)";
          c.font = "bold 22px system-ui";
          c.fillText(`Kalibratie: tik hoek ${tapCorners.length + 1} van 4 (LB→RB→RO→LO)`, 20, 40);
        }
      },
      { useVideoFrameCallback: true, targetFps: profile.fps },
    );
  }

  function sizeCanvas(vid: HTMLVideoElement): void {
    overlay.width = vid.videoWidth || 1280;
    overlay.height = vid.videoHeight || 720;
  }

  function onCanvasTap(ev: MouseEvent): void {
    if (!calibrating || !app.activeVideo) return;
    const rect = overlay.getBoundingClientRect();
    const scaleX = overlay.width / rect.width;
    const scaleY = overlay.height / rect.height;
    const p = { x: (ev.clientX - rect.left) * scaleX, y: (ev.clientY - rect.top) * scaleY };
    tapCorners.push(p);
    if (tapCorners.length === 4) {
      const cal = calibrationFromCorners(tapCorners, 16, 8);
      if (cal) {
        cal.teamsOnSides = app.config.setPlans[Math.max(0, app.engine.state.setNumber - 1)]?.startSides.A === "left" ? { left: "A", right: "B" } : { left: "B", right: "A" };
        app.saveCalibration(cal);
        app.motion.setFieldCalibration(cal);
        calStatus.textContent = "✅ Kalibratie opgeslagen (16 x 8 m veld, net halverwege).";
      } else {
        calStatus.textContent = "❌ Kalibratie mislukt (punten te ver in lijn?); tik opnieuw.";
        tapCorners = [];
      }
      calibrating = false;
    }
  }

  function beginCalibrate(): void {
    if (!app.activeVideo) {
      calStatus.textContent = "Start eerst een bron.";
      return;
    }
    calibrating = true;
    tapCorners = [];
  }

  function applySideFix(): void {
    if (!app.calibration) {
      calStatus.textContent = "Eerst kalibreren.";
      return;
    }
    const cur = app.calibration.teamsOnSides;
    app.calibration.teamsOnSides = { left: cur.right, right: cur.left };
    app.saveCalibration(app.calibration);
    app.motion.setFieldCalibration(app.calibration);
    calStatus.textContent = "Team-zijden in de kalibratie omgedraaid (kalibratie alleen).";
  }

  async function saveClip(): Promise<void> {
    const blob = app.clipCapture.snapshot();
    if (!blob) {
      calStatus.textContent = "Geen clip beschikbaar (recorder actief na bron-start).";
      return;
    }
    const meta = {
      label: "undecided" as const,
      reason: "unknown" as const,
      note: "",
      endOffsetMs: 0,
      framesProcessed: app.cameraStats().framesProcessed,
      fps: app.cameraStats().fps,
    };
    const id = await app.clipStore.saveClip(blob, meta);
    calStatus.textContent = `💾 Clip opgeslagen (${id}) — label hem hieronder.`;
    await refreshClips();
  }

  async function refreshClips(): Promise<void> {
    const clips = await app.clipStore.listClips();
    clipsList.innerHTML = "";
    if (clips.length === 0) {
      clipsList.append(h("p", { class: "muted small" }, ["Nog geen clips."]));
      return;
    }
    for (const c of clips.slice(0, 12)) {
      const reason = h("select", { class: "small" }, [
        h("option", { value: "ball_down_in_court", selected: c.reason === "ball_down_in_court" }, ["Bal in veld"]),
        h("option", { value: "ball_out_after_last_touch", selected: c.reason === "ball_out_after_last_touch" }, ["Bal uit"]),
        h("option", { value: "serve_fault", selected: c.reason === "serve_fault" }, ["Opslagfout"]),
        h("option", { value: "net_or_contact_fault", selected: c.reason === "net_or_contact_fault" }, ["Netfout"]),
        h("option", { value: "unknown", selected: c.reason === "unknown" }, ["Onduidelijk"]),
      ]);
      clipsList.append(
        h("div", { class: "clip-row" }, [
          h("span", { class: "small" }, [c.label === "A" ? "🅰" : c.label === "B" ? "🅱" : "❔", " ", c.reason, " ", new Date(c.createdAt).toLocaleTimeString()]),
          h("button", { class: "btn tiny", onclick: () => void labelClip(c, "A", reason.value as LabeledClip["reason"]) }, ["Punt A"]),
          h("button", { class: "btn tiny", onclick: () => void labelClip(c, "B", reason.value as LabeledClip["reason"]) }, ["Punt B"]),
          h("button", { class: "btn tiny", onclick: () => void labelClip(c, "undecided", reason.value as LabeledClip["reason"]) }, ["Onduidelijk"]),
          h("button", { class: "btn tiny ghost", onclick: () => void app.clipStore.deleteClip(c.id).then(refreshClips) }, ["🗑"]),
        ]),
      );
    }
  }

  async function labelClip(c: LabeledClip, label: LabeledClip["label"], reason: LabeledClip["reason"]): Promise<void> {
    await app.clipStore.setLabel(c.id, label, reason);
    calStatus.textContent = `Gelabeld: ${label}`;
    await refreshClips();
  }

  async function exportClips(): Promise<void> {
    const manifest = await app.clipStore.exportManifest();
    const json = JSON.stringify({ exportedAt: new Date().toISOString(), clips: manifest.clips }, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    downloadBlob(blob, `beachclips-manifest-${Date.now()}.json`);
    calStatus.textContent = `Manifest geëxporteerd met ${manifest.clips.length} clips (lokaal, geen upload).`;
  }

  overlay.addEventListener("click", onCanvasTap);

  const calibrateBlock = h("div", { class: "card" }, [
    h("legend", {}, ["Veldkalibratie"]),
    h("p", { class: "muted small" }, [
      "Tik de vier veldhoeken in de volgorde linksboven → rechtsboven → rechtsonder → linksonder. Zonder kalibratie neemt de app GEEN stellige in/uit-beslissingen.",
    ]),
    h("div", { class: "btn-row" }, [
      btn("✏ Tik-aanmaken (4 hoeken)", beginCalibrate),
      btn("↔ Team-zijden omdraaien", applySideFix),
      btn("Reset", () => {
        app.saveCalibration(null);
        app.motion.setFieldCalibration(null);
        calStatus.textContent = "Kalibratie gewist.";
      }, "btn small ghost"),
    ]),
    calStatus,
  ]);

  const measureBlock = h("div", { class: "card" }, [
    h("legend", {}, ["Meetmodus"]),
    h("p", { class: "muted small" }, [
      "Meet het ALVOOR je automatische scoring vertrouwt: effectieve FPS, gemiste frames, verwerkingstijd en geheugengebruik. Bewaar korte clips rond een vermoedelijk rally-einde en label ze; export blijft lokaal (geen automatische upload).",
    ]),
    statsEl,
    h("div", { class: "btn-row" }, [
      btn("💾 Bewaar clip", () => void saveClip()),
      btn("📤 Export manifest", () => void exportClips()),
    ]),
    clipsList,
  ]);

  const sideNote = h("p", { class: "muted small" }, [
    "Belangrijk: als de fysieke zijden wisselen (kantwissel), pas je de mapping aan via Scorebord → 'Zijden omwisselen'; de kalibratie hier geldt alleen voor de veldhoeken.",
  ]);

  root.append(
    h("div", { class: "topnav" }, [
      h("button", { class: "btn small ghost", onclick: () => app.go("live") }, ["← Scorebord"]),
      h("span", { class: "muted small" }, ["Camera & meetmodus (fase 2)"]),
    ]),
    wrap,
    visionEl,
    h("div", { class: "btn-row" }, [
      btn("▶ Start bron", () => void startSource(), "btn primary"),
      btn(app.visionOn ? "⏸ Vision uit" : "🎯 Vision aan", toggleVision, "btn warn"),
      btn("⏹ Stop", stopSource, "btn ghost"),
      btn("📷 Camera's", refreshCams, "btn small ghost"),
    ]),
    h("div", { class: "inline small" }, [sourceKind, camSelect, fileInput]),
    calibrateBlock,
    h("div", { class: "card" }, [
      h("legend", {}, ["📷 Bron-instellingen"]),
      h("p", { class: "muted small" }, [
        `Doelapparaat/profiel: ${app.config.perfProfile} (process ${profile.w}px, max ${profile.fps} fps). Kalibratie van 4 hoeken geeft een homografie naar een 16×8 m veld (net op 8 m).`,
      ]),
    ]),
    measureBlock,
    sideNote,
  );

  refreshCams();
  // Opschonen bij vertrek
  const cleanup = (): void => {
    app.frameLoop.stop();
    stopStats();
    window.removeEventListener("pagehide", cleanup);
  };
  window.addEventListener("pagehide", cleanup);
}
